import { computed, ComputedRef, inject, ref, Ref, watch, type InjectionKey } from 'vue';

import { getTenantsDropdownData } from '@/services/multitenancy.service';
import { TenantOption } from '@/components/tenancy-dropdown/type';
import { useQuery } from '@tanstack/vue-query';
import { useImpersonationInfo } from '@/hooks/useImpersonationInfo';
import { TenantList, TenantType } from '@/entities/tenant.entity';
import { AccessPolicies, AccessPolicy, PermissionScopeEnum } from '@/entities/role.entity';
import { hasAccess, isMultiTenancyEnabled } from '@/utils/user.utils';
import { PERMISSIONS } from '@/app-constants/permission.constants';
import { useRouter } from 'vue-router';
import { isEmpty, isEqual, some, uniqBy } from 'lodash';
import { useModuleStateStore } from '@/stores/moduleState.store';
import { usePartnerTenantStore } from '@/stores/partnerTenant.storage';
import { ROUTER } from '@/app-constants/router.constant';
import { useAccountInformation } from './useAccountInformation';
import { type FilterMode } from '@/components/tenancy-dropdown/type';

export type { FilterMode };

export const FETCH_RELATED_TENANTS_QK = 'fetch-user-related-tenants';

/** Injection key for render-mode tenant ID (provided by ReportRenderView). */
export const RENDER_TENANT_ID_KEY: InjectionKey<Ref<string | null>> = Symbol('renderTenantId');

type MultiTenancyDropdownContext = {
  tenants: Ref<TenantList>;
  isError: Ref<boolean>;
  isRelatedTenantsFullyLoaded: Ref<boolean>;
  isLoadingRelatedTenants: Ref<boolean>;
  tenantDropdownOptions: Ref<TenantOption[]>;
  isEmptyChildTenants: Ref<boolean>;

  dropdownPlaceholder: ComputedRef<string>;

  checkPermission: ComputedRef<(accessPolicy: AccessPolicy) => boolean>;
  shouldDropdownVisible: ComputedRef<boolean>;
  userTenantId: Ref<string>;
  fetchRelatedTenants: () => unknown;
  isDropdownDisable: ComputedRef<boolean>;

  // permission support
  hasSelfReadPermission: ComputedRef<boolean>;
  hasRelatedReadPermission: ComputedRef<boolean>;
  hasRelatedPermission: ComputedRef<(permission: string) => boolean>;
  hasSelfPermission: ComputedRef<(permission: string) => boolean>;
  hasNoDefaultTenant: ComputedRef<boolean>;
  hasSelfCreatePermission: ComputedRef<boolean>;
  hasRelatedCreatePermission: ComputedRef<boolean>;

  routeModules: Ref<string[]>;

  // tenant selection
  selectedTenant: Ref<string | undefined>;
  currentSelectedTenant: Ref<TenantOption>;

  // support multiple tenant selections
  selectedTenants: Ref<string[]>;

  setSelectedTenant: (v?: string) => void;
  setSelectedTenants: (v?: string[]) => void;

  isMultitenancyEnabled: Ref<boolean>;

  // tag filtering
  filterMode: Ref<FilterMode>;
  selectedTags: Ref<string[]>;
  tagDropdownOptions: ComputedRef<TenantOption[]>;
  tagFilteringAvailable: ComputedRef<boolean>;
  setFilterMode: (mode: FilterMode) => void;
  setSelectedTags: (tags: string[]) => void;

  // select-all (parent-account) filtering
  selectAllAccounts: Ref<boolean>;
  setSelectAllAccounts: (v: boolean) => void;
  parentTenantId: ComputedRef<string>;
};

const EMPTY_LIST_NUMBER = 0;

const useMultitenancy = ({
  tenantType = undefined,
  addUserTenant = true,
  shouldRun = false,
  includeSoldProducts = false,
}: {
  tenantType?: TenantType;
  addUserTenant?: boolean;
  shouldRun?: boolean;
  includeSoldProducts?: boolean;
} = {}): MultiTenancyDropdownContext => {
  // In PDF render mode, bypass multitenancy permission flow — use the tenant from the render token
  const renderTenantId = inject(RENDER_TENANT_ID_KEY, ref(null));

  const { impersonatedUser } = useImpersonationInfo();
  const {
    hasTechnologyChronicle,
    hasTechnologySentinelOne,
    hasTechnologyCrowdStrike,
    hasProductEmailSecured,
    hasProductSIEM,
    hasProductEDR,
    hasProductVulnerability,
    isSuccess: fetchingAccountInformationSuccess,
  } = useAccountInformation();
  const moduleStateStore = useModuleStateStore();
  const { getTenantSelection, getTenantSelections } = moduleStateStore;
  const partnerTenantStore = usePartnerTenantStore();
  const {
    data: tenants,
    isFetching: isLoadingRelatedTenants,
    isError,
    isSuccess,
    refetch: fetchRelatedTenants,
  } = useQuery({
    enabled: false,
    queryKey: [FETCH_RELATED_TENANTS_QK, includeSoldProducts],
    queryFn: async ({ signal }) => {
      return getTenantsDropdownData(
        partnerTenantStore.tenant?.id ?? impersonatedUser.tenantId,
        tenantType,
        undefined,
        signal,
        includeSoldProducts,
      );
    },
    refetchOnWindowFocus: false,
  });

  const selectedTenant = ref<string>();
  const currentSelectedTenant = ref<TenantOption>();
  const selectedTenants = ref<string[]>([]);
  const router = useRouter();
  const routeModules = ref<string[]>([]);
  const isMultitenancyEnabled = ref(false);

  const isAccessPolicies = (policy: AccessPolicy | AccessPolicies): policy is AccessPolicies => {
    return (policy as AccessPolicies).type !== undefined;
  };

  const calculateRouteModules = (): void => {
    const routeModuleSet = new Set<string>();
    const currentRoute = router.currentRoute.value;
    const routeAccessPolicy = currentRoute.meta?.accessPolicy as AccessPolicy | AccessPolicies;

    if (routeAccessPolicy) {
      if (isAccessPolicies(routeAccessPolicy)) {
        routeAccessPolicy.policies.forEach((p) => {
          routeModuleSet.add(p.moduleName);
        });
      } else {
        routeModuleSet.add(routeAccessPolicy.moduleName);
      }

      routeModules.value = Array.from(routeModuleSet);
    }
  };

  const hasSelfReadPermission = computed(() => {
    // Render mode: always allow self-read so tenant selection proceeds
    if (renderTenantId.value) return true;
    return some(Array.from(routeModules.value), (moduleName: string) =>
      hasAccess(impersonatedUser, {
        moduleName,
        permission: PERMISSIONS.READ,
        scope: PermissionScopeEnum.self,
      }),
    );
  });

  const hasRelatedReadPermission = computed(() => {
    return some(Array.from(routeModules.value), (moduleName: string) =>
      hasAccess(impersonatedUser, {
        moduleName,
        permission: PERMISSIONS.READ,
        scope: PermissionScopeEnum.related,
      }),
    );
  });

  const hasRelatedCreatePermission = computed(() => {
    return some(Array.from(routeModules.value), (moduleName: string) =>
      hasAccess(impersonatedUser, {
        moduleName,
        permission: PERMISSIONS.CREATE,
        scope: PermissionScopeEnum.related,
      }),
    );
  });

  const hasSelfCreatePermission = computed(() => {
    return some(Array.from(routeModules.value), (moduleName: string) =>
      hasAccess(impersonatedUser, {
        moduleName,
        permission: PERMISSIONS.CREATE,
        scope: PermissionScopeEnum.self,
      }),
    );
  });

  const hasRelatedPermission = computed(() => {
    return (permission: string): boolean =>
      some(Array.from(routeModules.value), (moduleName: string) =>
        hasAccess(impersonatedUser, {
          scope: PermissionScopeEnum.related,
          moduleName,
          permission,
        }),
      );
  });

  const hasSelfPermission = computed(() => {
    return (permission: string): boolean =>
      some(Array.from(routeModules.value), (moduleName: string) =>
        hasAccess(impersonatedUser, {
          scope: PermissionScopeEnum.self,
          moduleName,
          permission,
        }),
      );
  });

  const hasNoDefaultTenant = computed(() => !hasSelfReadPermission.value && hasRelatedReadPermission.value);

  const tenantDropdownOptions = computed(() => {
    let options: TenantOption[] = [];

    if (hasAccess(impersonatedUser, ROUTER.PARTNER.LANDING_PAGE.accessPolicy) && !!partnerTenantStore.tenant) {
      options = [
        ...options,
        {
          id: partnerTenantStore.tenant?.id,
          name: partnerTenantStore.tenant?.tenantName,
          tags: partnerTenantStore.tenant?.tags || [],
          tenantType: partnerTenantStore.tenant?.tenantType,
          isSecurityPostureSurveyActivated: partnerTenantStore.tenant?.isSecurityPostureSurveyActivated,
          isSecurityPostureSurveyCompleted: partnerTenantStore.tenant?.isSecurityPostureSurveyCompleted,
          tenantAbbreviation: partnerTenantStore.tenant?.tenantAbbreviation,
          ...(!isEmpty(partnerTenantStore.tenant?.soldProducts)
            ? { soldProducts: partnerTenantStore.tenant?.soldProducts }
            : {}),
        },
      ];
    } else if (addUserTenant && impersonatedUser.tenant && hasSelfReadPermission.value) {
      options = [
        ...options,
        {
          id: impersonatedUser.tenantId,
          name: impersonatedUser.tenant.tenantName,
          tags: impersonatedUser.tenant.tags || [],
          tenantType: impersonatedUser.tenant.tenantType,
          isSecurityPostureSurveyActivated: impersonatedUser.tenant?.isSecurityPostureSurveyActivated,
          isSecurityPostureSurveyCompleted: impersonatedUser.tenant?.isSecurityPostureSurveyCompleted,
          tenantAbbreviation: impersonatedUser.tenant?.tenantAbbreviation,
          ...(!isEmpty(impersonatedUser.tenant?.soldProducts)
            ? { soldProducts: impersonatedUser.tenant?.soldProducts }
            : {}),
        },
      ];
    }

    if (hasRelatedReadPermission.value) {
      const relatedTenantList = (tenants.value ?? []).map(
        ({
          id,
          tenantName,
          tags,
          tenantType,
          isSecurityPostureSurveyActivated,
          isSecurityPostureSurveyCompleted,
          tenantAbbreviation,
          soldProducts,
        }) => ({
          id,
          name: tenantName,
          tags,
          tenantType,
          isSecurityPostureSurveyActivated,
          isSecurityPostureSurveyCompleted,
          tenantAbbreviation,
          ...(!isEmpty(soldProducts) ? { soldProducts } : {}),
        }),
      );
      options = [...options, ...relatedTenantList];
    }

    return options;
  });

  const isEmptyChildTenants = computed(() => {
    return (
      tenants.value?.length === EMPTY_LIST_NUMBER &&
      !(hasAccess(impersonatedUser, ROUTER.PARTNER.LANDING_PAGE.accessPolicy) && !!partnerTenantStore.tenant)
    );
  });

  const checkPermission = computed(() => {
    return (accessPolicy: AccessPolicy): boolean => hasAccess(impersonatedUser, accessPolicy);
  });

  const checkMultitenancyEnable = (): void => {
    isMultitenancyEnabled.value = isMultiTenancyEnabled(impersonatedUser);
  };

  // In PDF render mode, the auth store only holds the access token (no
  // impersonatedUser). Fall back to the tenant id from the render token so
  // queries gated on userTenantId still resolve a real tenant.
  const userTenantId = computed(() => impersonatedUser?.tenantId ?? renderTenantId.value ?? '');

  const shouldDropdownVisible = computed(() => {
    return (
      isMultitenancyEnabled.value &&
      hasRelatedReadPermission.value &&
      (!hasSelfReadPermission.value || !isEmptyChildTenants.value)
    );
  });

  // Tag filtering: tags become the selectable items in place of tenants.
  const filterMode = ref<FilterMode>(moduleStateStore.getFilterMode());
  const selectedTags = ref<string[]>(moduleStateStore.getTagSelections());

  // Unique, active tags drawn from the related tenants, shaped as dropdown
  // options (id = tag snow_id, name = short_description). This lets the
  // existing tenant dropdown render tags without modification.
  const tagDropdownOptions = computed<TenantOption[]>(() => {
    const tagOptions = (tenantDropdownOptions.value ?? [])
      .flatMap((tenant) => tenant.tags ?? [])
      .filter((tag) => tag?.snowId && tag?.active !== false)
      .map((tag) => ({
        id: tag.snowId as string,
        name: tag.shortDescription || tag.name,
        tags: [],
        tenantType: undefined,
        tenantAbbreviation: '',
      }));

    return uniqBy(tagOptions, 'id');
  });

  // Tag mode is offered only when the dropdown is visible AND there is at
  // least one active tag among the (descendant) tenants.
  const tagFilteringAvailable = computed(() => {
    return shouldDropdownVisible.value && tagDropdownOptions.value.length > 0;
  });

  const setFilterMode = (mode: FilterMode): void => {
    filterMode.value = mode;
    moduleStateStore.keepFilterMode(mode);
  };

  // Persisted tag mode is invalid when the current tenant set has no tags.
  watch(
    tagFilteringAvailable,
    (available) => {
      if (!available && filterMode.value === 'tags') {
        setFilterMode('accounts');
      }
    },
    { immediate: true },
  );

  const setSelectedTags = (tags: string[]): void => {
    selectedTags.value = tags;
    moduleStateStore.keepTagSelections(tags);
  };

  // Select-all is represented as a flag plus the parent tenant id, so the
  // backend can filter cases by parent account instead of receiving every
  // descendant tenant id.
  const selectAllAccounts = ref<boolean>(moduleStateStore.getSelectAllAccounts());

  const setSelectAllAccounts = (v: boolean): void => {
    selectAllAccounts.value = v;
    moduleStateStore.keepSelectAllAccounts(v);
  };

  // The root tenant whose descendants make up "all": the partner-selected
  // tenant when browsing as a partner, otherwise the user's own tenant.
  const parentTenantId = computed(() => {
    if (hasAccess(impersonatedUser, ROUTER.PARTNER.LANDING_PAGE.accessPolicy) && !!partnerTenantStore.tenant) {
      return partnerTenantStore.tenant?.id;
    }
    return userTenantId.value;
  });

  const setSelectedTenants = (v?: string[]): void => {
    if (v) {
      selectedTenants.value = v;

      return;
    }

    // Render mode: use tenant from render token, skip permission-based logic
    if (renderTenantId.value) {
      selectedTenants.value = [renderTenantId.value];
      return;
    }

    let tenantIds = hasSelfReadPermission.value ? [userTenantId.value] : [];

    if (hasAccess(impersonatedUser, ROUTER.PARTNER.LANDING_PAGE.accessPolicy) && !!partnerTenantStore.tenant) {
      tenantIds = [partnerTenantStore.tenant?.id];
    }

    const previousTenantSelections = getTenantSelections();
    if (!isEmpty(previousTenantSelections)) {
      tenantIds = [...previousTenantSelections];
      // filter by self
      tenantIds = tenantIds.filter((tenantId: string) => {
        if (isEqual(tenantId, userTenantId.value)) {
          return hasSelfReadPermission.value;
        }

        return true;
      });
      // filter by related
      tenantIds = tenantIds.filter((tenantId: string) => {
        if (!isEqual(tenantId, userTenantId.value)) {
          return hasRelatedReadPermission.value;
        }

        return true;
      });
    }

    selectedTenants.value = tenantIds;
  };

  const setSelectedTenant = (v?: string): void => {
    if (v) {
      selectedTenant.value = v;

      return;
    }

    // Render mode: use tenant from render token, skip permission-based logic
    if (renderTenantId.value) {
      selectedTenant.value = renderTenantId.value;
      return;
    }

    let tenantId = hasSelfReadPermission.value ? userTenantId.value : null;

    // check if the user is a partner user and is viewing its child tenants
    if (hasAccess(impersonatedUser, ROUTER.PARTNER.LANDING_PAGE.accessPolicy) && !!partnerTenantStore.tenant) {
      tenantId = partnerTenantStore.tenant?.id;
    }

    const previousTenantSelection = getTenantSelection();

    if (
      !isEmpty(previousTenantSelection) &&
      // user assigned tenant === previous tenant selection
      ((hasSelfReadPermission.value && isEqual(userTenantId.value, previousTenantSelection)) ||
        // previous tenant selection === child tenant
        (hasRelatedReadPermission.value && !isEqual(userTenantId.value, previousTenantSelection)))
    ) {
      tenantId = previousTenantSelection;
    }

    if (tenantId) {
      selectedTenant.value = tenantId;

      return;
    }

    selectedTenant.value = '';
  };

  watch(
    [
      (): unknown => selectedTenant.value,
      (): unknown => isLoadingRelatedTenants.value,
      (): unknown => fetchingAccountInformationSuccess.value,
    ],
    () => {
      // Render mode: no impersonatedUser, no tenant dropdown — build TenantOption from
      // render token tenant id + account info (sold products / technologies).
      if (renderTenantId.value && fetchingAccountInformationSuccess.value) {
        currentSelectedTenant.value = {
          id: renderTenantId.value,
          name: '',
          tags: [],
          tenantType: undefined,
          tenantAbbreviation: '',
          soldProducts: {
            hasChronicle: hasTechnologyChronicle.value,
            hasSentinelone: hasTechnologySentinelOne.value,
            hasCrowdstrike: hasTechnologyCrowdStrike.value,
            hasVulnerabilityManagement: hasProductVulnerability.value,
            hasEmailSecurity: hasProductEmailSecured.value,
            hasSiem: hasProductSIEM.value,
            hasEdr: hasProductEDR.value,
          },
        };
        return;
      }

      if (impersonatedUser.tenantId === selectedTenant.value && fetchingAccountInformationSuccess.value) {
        currentSelectedTenant.value = {
          id: impersonatedUser.tenantId,
          name: impersonatedUser.tenant.tenantName,
          tags: impersonatedUser.tenant.tags || [],
          tenantType: impersonatedUser.tenant.tenantType,
          isSecurityPostureSurveyActivated: impersonatedUser.tenant?.isSecurityPostureSurveyActivated,
          isSecurityPostureSurveyCompleted: impersonatedUser.tenant?.isSecurityPostureSurveyCompleted,
          tenantAbbreviation: impersonatedUser.tenant?.tenantAbbreviation,
          soldProducts: {
            hasChronicle: hasTechnologyChronicle.value,
            hasSentinelone: hasTechnologySentinelOne.value,
            hasCrowdstrike: hasTechnologyCrowdStrike.value,
            hasVulnerabilityManagement: hasProductVulnerability.value,
            hasEmailSecurity: hasProductEmailSecured.value,
            hasSiem: hasProductSIEM.value,
            hasEdr: hasProductEDR.value,
          },
        };
      } else {
        const singleSelectedTenant = tenantDropdownOptions.value?.find((t) => t.id === selectedTenant.value);
        currentSelectedTenant.value = singleSelectedTenant;
      }
    },
    {
      immediate: true,
    },
  );

  const isDropdownDisable = computed(() => {
    return hasRelatedReadPermission.value && !hasSelfReadPermission.value && isEmptyChildTenants.value;
  });

  const dropdownPlaceholder = computed(() => {
    if (isDropdownDisable.value) {
      return 'No tenants found';
    }

    if (hasRelatedReadPermission.value && !hasSelfReadPermission.value && !isEmptyChildTenants.value) {
      return 'Select tenant';
    }

    return '';
  });

  if (shouldRun && hasRelatedReadPermission.value) {
    fetchRelatedTenants();
  }

  watch(() => router.currentRoute.value.fullPath, calculateRouteModules, { immediate: true });

  watch(
    isLoadingRelatedTenants,
    () => {
      checkMultitenancyEnable();
    },
    {
      immediate: true,
    },
  );

  return {
    tenantDropdownOptions,
    tenants,
    isError,
    isRelatedTenantsFullyLoaded: isSuccess,
    isLoadingRelatedTenants,
    isEmptyChildTenants,
    fetchRelatedTenants,
    shouldDropdownVisible,

    // props
    dropdownPlaceholder,

    // user properties
    userTenantId,

    // supporting functions
    checkPermission,
    hasRelatedReadPermission,
    hasSelfReadPermission,
    hasRelatedPermission,
    hasSelfPermission,
    hasRelatedCreatePermission,
    hasSelfCreatePermission,
    hasNoDefaultTenant,
    isDropdownDisable,

    routeModules,

    // tenant selection
    selectedTenant,
    currentSelectedTenant,
    selectedTenants,
    setSelectedTenants,
    setSelectedTenant,

    isMultitenancyEnabled,

    // tag filtering
    filterMode,
    selectedTags,
    tagDropdownOptions,
    tagFilteringAvailable,
    setFilterMode,
    setSelectedTags,

    // select-all (parent-account) filtering
    selectAllAccounts,
    setSelectAllAccounts,
    parentTenantId,
  };
};

export default useMultitenancy;
